import React from 'react';
import styles from '../NDAGenerator.module.scss';
import { CreateModeProps, FormDataShape, TemplateOption } from './types';

const getInputType = (key: string): 'text' | 'email' | 'date' => {
  if (key === 'date') return 'date';
  if (key.toLowerCase().includes('mail')) return 'email';
  return 'text';
};

const formFields = [
  { key: 'xCompany', label: 'Counterparty Name' },
  { key: 'xAddress', label: 'Counterparty Address' },
  { key: 'xMail', label: 'Counterparty Email' },
  { key: 'zurichAddress', label: 'Zurich Address' },
  { key: 'zurichMail', label: 'Zurich Email' },
  { key: 'project', label: 'Project/Purpose' },
  { key: 'date', label: 'Effective Date' },
  { key: 'xSigner', label: 'Counterparty Signer' },
  { key: 'zurichSigner', label: 'Zurich Signer' }
];

export const CreateMode: React.FC<CreateModeProps> = ({
  templates,
  visibleTemplates,
  templatesLoading,
  templatesError,
  selectedTemplateId,
  onSelectTemplateId,
  selectedTemplate,
  selectedLanguage,
  languageOptions,
  onSelectLanguage,
  formData,
  setFormData,
  onFieldChange,
  isFormValid,
  showPreview,
  setShowPreview,
  downloading,
  onDownload
}) => {
  return (
    <div className={styles.leftPanel}>
      <h2>NDA Generator</h2>
      {showPreview ? (
        <div className={styles.previewContainer}>
          <div className={styles.previewActions}>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className={styles.editButton}
            >
              ← Edit Form
            </button>
            <button
              type="button"
              onClick={onDownload}
              className={styles.downloadButton}
              disabled={downloading}
            >
              {downloading ? 'Generating...' : 'Download .docx'}
            </button>
          </div>
          <div className={styles.previewContent}>
            <h3>NDA Preview — {formData.ndaType} Agreement</h3>
            <div className={styles.previewSection}>
              <h4>Counterparty Information</h4>
              {Boolean(formData.xCompany?.trim()) && (
                <p>
                  <strong>Company:</strong> {formData.xCompany}
                </p>
              )}
              {Boolean(formData.xAddress?.trim()) && (
                <p>
                  <strong>Address:</strong> {formData.xAddress}
                </p>
              )}
              {Boolean(formData.xMail?.trim()) && (
                <p>
                  <strong>Email:</strong> {formData.xMail}
                </p>
              )}
              {Boolean(formData.xSigner?.trim()) && (
                <p>
                  <strong>Signer:</strong> {formData.xSigner}
                </p>
              )}
            </div>
            <div className={styles.previewSection}>
              <h4>Zurich Information</h4>
              {Boolean(formData.zurichAddress?.trim()) && (
                <p>
                  <strong>Address:</strong> {formData.zurichAddress}
                </p>
              )}
              {Boolean(formData.zurichMail?.trim()) && (
                <p>
                  <strong>Email:</strong> {formData.zurichMail}
                </p>
              )}
              {Boolean(formData.zurichSigner?.trim()) && (
                <p>
                  <strong>Signer:</strong> {formData.zurichSigner}
                </p>
              )}
            </div>
            <div className={styles.previewSection}>
              <h4>Agreement Details</h4>
              {Boolean(formData.project?.trim()) && (
                <p>
                  <strong>Project:</strong> {formData.project}
                </p>
              )}
              {Boolean(formData.date?.trim()) && (
                <p>
                  <strong>Effective Date:</strong> {formData.date}
                </p>
              )}
              <p>
                <strong>NDA Type:</strong> {formData.ndaType}
              </p>
              <p>
                <strong>Publish price-sensitive information:</strong>{' '}
                {formData.showDraftingNote ? 'Yes' : 'No'}
              </p>
            </div>
            {selectedTemplate && (
              <div className={styles.previewSection}>
                <h4>Template</h4>
                <p>
                  <strong>Selected:</strong> {selectedTemplate.label}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();

            setShowPreview(true);
          }}
        >
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label htmlFor="templateId" className={styles.label}>
                Template
              </label>
              <select
                id="templateId"
                value={selectedTemplateId}
                onChange={(e) => {
                  const id = e.target.value;
                  onSelectTemplateId(id);
                  const t = templates.find((tpl) => tpl.id === id);
                  if (t?.languageCode) {
                    onSelectLanguage(t.languageCode);
                  }
                }}
                className={styles.select}
                disabled={templatesLoading}
              >
                {visibleTemplates.map((t: TemplateOption) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              {templatesError && (
                <div className={styles.errorMessage} role="alert">
                  {templatesError}
                </div>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="languageCode" className={styles.label}>
                Language
              </label>
              <select
                id="languageCode"
                value={selectedLanguage}
                onChange={(e) => {
                  const newLang = e.target.value;
                  onSelectLanguage(newLang);

                  const matchingTemplate = templates.find(
                    (t) => (t.languageCode ?? 'en') === newLang
                  );
                  if (matchingTemplate) {
                    onSelectTemplateId(matchingTemplate.id);
                  }
                }}
                className={styles.select}
              >
                {languageOptions.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.grid}>
            {formFields.map((field) => {
              const value = formData[
                field.key as keyof FormDataShape
              ] as string;
              const type = getInputType(field.key);
              const filled = Boolean(value);
              return (
                <div key={field.key} className={styles.inputGroup}>
                  <input
                    type={type}
                    value={value}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFormData((f) => ({
                        ...f,
                        [field.key]: next
                      }));
                      if (onFieldChange) {
                        onFieldChange(field.key as keyof FormDataShape, next);
                      }
                    }}
                    className={`${styles.input} ${filled ? styles.filled : ''}`}
                    placeholder={field.label}
                    required
                  />
                  <label
                    className={`${styles.floatingLabel} ${
                      filled ? styles.filled : ''
                    }`}
                  >
                    {field.label}
                  </label>
                </div>
              );
            })}
          </div>
          <div className={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="showDraftingNote"
              checked={formData.showDraftingNote === true}
              onChange={(e) =>
                setFormData((f) => ({
                  ...f,
                  showDraftingNote: e.target.checked
                }))
              }
              className={styles.checkbox}
            />
            <label htmlFor="showDraftingNote" className={styles.checkboxLabel}>
              Publish price-sensitive information
            </label>
          </div>
          <div className={styles.buttonContainer}>
            <button
              type="submit"
              className={styles.reviewButton}
              disabled={!isFormValid}
            >
              Review & Preview
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateMode;
